/**
 * The passenger's in-flight journey search.
 *
 * This is genuine client state and the only reason a slice exists for it: the
 * search is composed on one screen (pick-up, destination, date, seats), the
 * results are shown on another, and booking from a result has to quote the
 * search's id so the API can attribute the booking to it. None of that is a
 * server resource, so none of it belongs in the query cache.
 *
 * The results themselves are not kept here. `searchRides` is an RTK Query
 * mutation and its result is read from the hook; copying it into a slice would
 * create a second copy that goes stale the moment the search is re-run.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Point, RideType, SearchResult, UUID } from "../../api/types";

export interface JourneyDraft {
    startAddress: string;
    endAddress: string;
    origin: Point | null;
    destination: Point | null;
    /** RFC3339, built from the date and time inputs before it is sent. */
    departureAt: string;
    seats: number;
    rideType: RideType;
    childOnBoard: boolean;
}

export interface RideSearchState {
    draft: JourneyDraft;
    /**
     * The id of the stored search the current results came from. Sent back with
     * a booking so the API can tie the two together.
     */
    requestId: UUID | null;
    /** The match the passenger is currently looking at, by ride id. */
    selectedRideId: UUID | null;
}

export const emptyJourney: JourneyDraft = {
    startAddress: "",
    endAddress: "",
    origin: null,
    destination: null,
    departureAt: "",
    seats: 1,
    rideType: "shared",
    childOnBoard: false,
};

const initialState: RideSearchState = {
    draft: emptyJourney,
    requestId: null,
    selectedRideId: null,
};

const rideSearchSlice = createSlice({
    name: "rideSearch",
    initialState,
    reducers: {
        journeyUpdated(state, action: PayloadAction<Partial<JourneyDraft>>) {
            state.draft = { ...state.draft, ...action.payload };
        },
        journeyCleared(state) {
            state.draft = emptyJourney;
            state.requestId = null;
            state.selectedRideId = null;
        },
        /** Records which search produced the results now on screen. */
        searchCompleted(state, action: PayloadAction<SearchResult>) {
            state.requestId = action.payload.requestId;
            state.selectedRideId = null;
        },
        rideSelected(state, action: PayloadAction<UUID | null>) {
            state.selectedRideId = action.payload;
        },
    },
});

export const { journeyUpdated, journeyCleared, searchCompleted, rideSelected } =
    rideSearchSlice.actions;

export default rideSearchSlice.reducer;
